import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_SIZE_CARD_DATA } from "../../DataTagConstants";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import "./DataEstateSizeCard.scss";
import { percentageCalculation } from "../../../../components/common/utilities";
import { filesize } from "filesize";
import { numberFormatter } from "../../../../components/common/Dashboard/utils";

const formatSize = (bytes) => {
  return filesize(bytes, { standard: "jedec" });
};

const calcPercent = (value, total) =>
  total > 0 ? ((value / total) * 100).toFixed(1) : 0;

const getOrCreateTooltipEl = (wrapper) => {
  let el = wrapper.querySelector(".de-chart-tooltip");

  if (!el) {
    el = document.createElement("div");
    el.className = "de-chart-tooltip";
    wrapper.appendChild(el);
  }

  return el;
};

const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;

  const wrapper = chart.canvas.closest(".data-estate-chart");

  if (!wrapper) return;

  const tooltipEl = getOrCreateTooltipEl(wrapper);

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  if (tooltip.body) {
    const lines = tooltip.body.map((b) => b.lines).flat();

    tooltipEl.innerHTML = lines.map((line) => `<div>${line}</div>`).join("");
  }

  const { offsetLeft: canvasX, offsetTop: canvasY } = chart.canvas;

  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${canvasX + tooltip.caretX}px`;
  tooltipEl.style.top = `${canvasY + tooltip.caretY}px`;
};

function DataEstateSizeCard() {
  const sizeData = useSelector((state) => state.dataTagReducer.sizeCardData);

  const dataSourceType = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.type,
  );

  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );

  const sensitiveCardData = useSelector(
    (state) => state.dataTagReducer.sensitiveCardData,
  );

  const SensitivityData = sensitiveCardData?.sensitiveData || [];

  const sensitivity_count_critical = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "CRITICAL")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_high = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "HIGH")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_low = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "LOW")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_medium = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "MEDIUM")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_no_risk = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "NO_RISK")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_not_processed = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "NOT_PROCESSED")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_processing_error = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "PROCESSING_ERROR")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const sensitivity_count_non_sensitive = Number(
    SensitivityData.find((d) => d["DataTagSensitivity.category"] === "NON_SENSITIVE")?.[
      "DataTagSensitivity.count"
    ] || 0,
  );

  const processedSensitivityCount = Number(sensitivity_count_high) + Number(sensitivity_count_medium) + Number(sensitivity_count_low) + Number(sensitivity_count_critical);
  const noRiskCount = Number(sensitivity_count_no_risk) + Number(sensitivity_count_non_sensitive) + Number(sensitivity_count_processing_error);


  const isDataBase = useSelector((state) => state.dataTagReducer.isDatabase);

  const dispatch = useDispatch();

  const isNTFS = dataSourceType === "NTFS";

  useEffect(() => {
    if (dataSourceId) {
      dispatch({
        type: FETCH_SIZE_CARD_DATA,
        payload: dataSourceId,
      });
    }
  }, [dataSourceId]);

  if (!sizeData) {
    return <div>Loading...</div>;
  }

  const sizeMB = sizeData?.size?.[0]?.["DataTagSensitivity.fileSizeTotal"] || 0;

  const pdaCount = isDataBase
    ? sizeData?.totalColumns?.[0]?.["DataguardDbViewTotal.totalColumns"] || 0
    : sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.publicFilesCount"] ||
      0;

  const nonPdaCount =
    Number(
      sizeData?.sensitiveData?.[0]?.[
        "DataTagSensitivity.publicAccessCheckedCount"
      ] || 0,
    ) -
    Number(
      sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.publicFilesCount"] ||
        0,
    );

  const pdaRemainingCount = Number(
    sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.notScannedCount"] || 0,
  );

  const regulatedCount = isDataBase
    ? sizeData?.regulatedTables?.[0]?.[
        "DataguardDbViewTotal.regulatedTables"
      ] || 0
    : sizeData?.regulatedFileCount?.[0]?.[
        "FileClassificationStats.fileCount"
      ] || 0;

  const sensitiveCount = isDataBase
    ? sizeData?.sensitiveColumns?.[0]?.[
        "DataguardDbViewTotal.sensitiveColumns"
      ] || 0
    : processedSensitivityCount || 0;

  const totalCount = isDataBase
    ? sizeData?.totalColumns?.[0]?.["DataguardDbViewTotal.totalColumns"] || 0
    : sizeData?.total?.[0]?.["DataTagSensitivity.count"] || 0;

  const totalTablesCount =
    sizeData?.totalTables?.[0]?.["DataguardDbViewTotal.totalTables"] || 0;

  const processedCount = isNTFS
    ? Number(
        sizeData?.processed?.[0]?.["DataTagSensitivity.ntfsProcessedCount"] ||
          0,
      )
    : Number(
        sizeData?.processed?.[0]?.["DataTagSensitivity.processedCount"] || 0,
      );

  const notProcessedCount = isNTFS
    ? Math.max(0, Number(totalCount) - processedCount)
    : Number(
        sizeData?.notProcessed?.[0]?.["DataTagSensitivity.notProcessedCount"] ||
          0,
      );

  const formattedSize = formatSize(sizeMB);

  const pdaPercent =
    percentageCalculation([{ count: pdaCount }], totalCount)[0]?.count || 0;

  const regulatedPercent =
    percentageCalculation([{ count: regulatedCount }], totalCount)[0]?.count ||
    0;

  const sensitivePercent =
    percentageCalculation([{ count: sensitiveCount }], totalCount)[0]?.count ||
    0;

  const processedPercent =
    totalCount > 0 ? (Number(processedCount) / Number(totalCount)) * 100 : 0;

  const notProcessedSensitivityCount = Math.max(
    0,
    Number(totalCount) -
      Number(processedSensitivityCount) -
      Number(noRiskCount),
  );

  const processedSensitivityPercent =
    totalCount > 0
      ? (Number(processedSensitivityCount) / Number(totalCount)) * 100
      : 0;

  const noRiskPercent =
    totalCount > 0 ? (Number(noRiskCount) / Number(totalCount)) * 100 : 0;

  const notProcessedSensitivityPercent = Math.max(
    0,
    100 - processedSensitivityPercent - noRiskPercent,
  );

  const doughnutData = {
    datasets: [
      {
        // OUTER RING
        labels: ["Processed", "Not Processed"],

        backgroundColor: ["#4caf50", "#e5e5e5"],

        data: [processedPercent, Math.max(0, 100 - processedPercent)],

        weight: 1,
        spacing: 4,
      },

      {
        // 2ND RING
        labels: ["Sensitive", "No Risk", "Not Processed"],

        backgroundColor: ["#ff3366", "#9b59b6", "#e5e5e5"], // red, purple, grey

        data: [
          processedSensitivityPercent,
          noRiskPercent,
          notProcessedSensitivityPercent,
        ],

        weight: 1,
        spacing: 4,
      },

      {
        // 3RD RING
        labels: ["Regulated", "Remaining"],

        backgroundColor: ["#ffa500", "#e5e5e5"],

        data: [regulatedPercent, Math.max(0, 100 - regulatedPercent)],

        weight: 1,
        spacing: 4,
      },
    ],
  };

  const processingPieData = {
    labels: ["Public", "Remaining"],

    datasets: [
      {
        data: [pdaPercent, Math.max(0, 100 - pdaPercent)],

        backgroundColor: ["#0090ff", "#e5e5e5"],

        borderWidth: 0,

        cutout: "65%",
      },
    ],
  };

  return (
    <div className="size-card">
      <div className="card-header size">
        <div>{isDataBase ? "Schema" : "Size"}</div>

        {!isDataBase && <div className="size-display">{formattedSize}</div>}
      </div>

      <div className="sizeCardLowerContainer">
        <div className="details">
          {/* TOTAL */}

          <div className="row">
            <div className="circle total"></div>

            <span>Total</span>

            <strong>{numberFormatter(totalCount)}</strong>
          </div>

          {/* SENSITIVITY */}

          <div className="row">
            <div className="circle sensitive"></div>

            <span>Sensitive</span>

            <strong>{sensitivePercent}%</strong>
          </div>

          {/* REGULATION */}

          <div className="row">
            <div className="circle regulated"></div>

            <span>Regulated</span>

            <strong>{regulatedPercent}%</strong>
          </div>

          {/* PUBLIC */}

          <div className="row">
            <div className="circle pda"></div>

            <span>Public</span>

            <strong>{isNTFS ? "N/A" : `${pdaPercent}%`}</strong>
          </div>
        </div>

        <div className="chart data-estate-chart">
          <Doughnut
            data={doughnutData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              devicePixelRatio: window.devicePixelRatio || 1,
              resizeDelay: 0,

              cutout: "42%",

              plugins: {
                legend: {
                  display: false,
                },

                datalabels: {
                  display: false,
                },

                tooltip: {
                  enabled: false,

                  external: externalTooltipHandler,

                  callbacks: {
                    label: (context) => {
                      const label =
                        context.dataset.labels?.[context.dataIndex] || "";

                      const datasetIndex = context.datasetIndex;

                      let value = 0;

                      if (datasetIndex === 0) {
                        if (label === "Processed") {
                          value = processedCount;
                        } else if (label === "Not Processed") {
                          value = notProcessedCount;
                        }
                      }

                      if (datasetIndex === 1) {
                        if (label === "Sensitive") {
                          value = processedSensitivityCount;
                        } else if (label === "No Risk") {
                          value = noRiskCount;
                        } else if (label === "Not Processed") {
                          value = notProcessedSensitivityCount;
                        }
                      }

                      if (datasetIndex === 2) {
                        if (label === "Regulated") {
                          value = regulatedCount;
                        } else if (label === "Remaining") {
                          value = Math.max(
                            0,
                            Number(totalCount) - Number(regulatedCount),
                          );
                        }
                      }

                      return `${label}: ${numberFormatter(value)}`;
                    },
                  },
                },
              },

              interaction: {
                mode: "nearest",
                intersect: true,
              },
            }}
          />

          <div className="processing-chart">
            <Doughnut
              data={processingPieData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                devicePixelRatio: window.devicePixelRatio || 1,
                resizeDelay: 0,

                cutout: "0%",

                plugins: {
                  legend: {
                    display: false,
                  },

                  datalabels: {
                    display: false,
                  },

                  tooltip: {
                    enabled: false,

                    external: externalTooltipHandler,

                    callbacks: {
                      label: (context) => {
                        if (isNTFS) {
                          return "Public: N/A";
                        }

                        const label = context.label;

                        let value = 0;

                        if (label === "Public") {
                          value = pdaCount;
                        } else if (label === "Remaining") {
                          value = Math.max(
                            0,
                            Number(totalCount) - Number(pdaCount),
                          );
                        }

                        return `${label}: ${numberFormatter(value)}`;
                      },
                    },
                  },
                },

                interaction: {
                  mode: "nearest",
                  intersect: true,
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataEstateSizeCard;
